"use client";

import React, { useEffect, useRef, useState } from "react";
import {
  AnimatePresence,
  motion,
  useInView,
  useReducedMotion,
} from "framer-motion";
import { Check, Mic, PhoneOff, Stethoscope, Video } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1] as const;
const AUTOPLAY_MS = 4500;

// ---------------------------------------------------------------------------
// Data shape — lives in the topic JSON (content.journey), only present for
// topics that want the interactive phone (currently telemedicine-software).
// ---------------------------------------------------------------------------
interface JourneyScreenBook {
  appName: string;
  heading: string;
  specialty: string;
  slots: string[];
  cta: string;
}
interface JourneyScreenCheckin {
  heading: string;
  items: string[];
  cta: string;
}
interface JourneyScreenVisit {
  clinicianLabel: string;
  duration: string;
  caption: string;
  endLabel: string;
}
interface JourneyScreenPrescription {
  heading: string;
  medication: string;
  pharmacy: string;
  status: string;
  cta: string;
}
interface JourneyScreenFollowup {
  heading: string;
  message: string;
  planItems: string[];
  cta: string;
}

export type HealthTechJourneyStep =
  | { id: "book"; label: string; description: string; screen: JourneyScreenBook }
  | { id: "checkin"; label: string; description: string; screen: JourneyScreenCheckin }
  | { id: "visit"; label: string; description: string; screen: JourneyScreenVisit }
  | { id: "prescription"; label: string; description: string; screen: JourneyScreenPrescription }
  | { id: "followup"; label: string; description: string; screen: JourneyScreenFollowup };

export interface HealthTechJourney {
  title: string;
  boldTitle?: string;
  description: string;
  steps: HealthTechJourneyStep[];
}

// ---------------------------------------------------------------------------
// Phone screens — five small fake-app layouts built from divs. The whole
// phone is decorative (aria-hidden on the frame): every piece of information
// shown here is also present as text in the step list.
// ---------------------------------------------------------------------------
function BookScreen({ s }: { s: JourneyScreenBook }) {
  return (
    <div className="flex h-full flex-col px-4 pb-4">
      <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-zinc-400">
        {s.appName}
      </p>
      <h3 className="mt-2 text-[15px] font-semibold leading-snug text-zinc-900">
        {s.heading}
      </h3>

      <div className="mt-4 flex items-center gap-3 rounded-xl bg-zinc-100 px-3 py-3">
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-white">
          <Stethoscope size={15} className="text-orange-500" />
        </span>
        <p className="text-[12px] font-semibold text-zinc-900">{s.specialty}</p>
      </div>

      <div className="mt-4 space-y-2">
        {s.slots.map((slot, i) => (
          <div
            key={slot}
            className={`rounded-lg border px-3 py-2.5 text-[12px] font-medium tabular-nums ${
              i === 1
                ? "border-orange-500 bg-orange-50 text-orange-600"
                : "border-zinc-200 text-zinc-600"
            }`}
          >
            {slot}
          </div>
        ))}
      </div>

      <div className="mt-auto rounded-full bg-orange-500 py-2.5 text-center text-[12px] font-semibold text-white">
        {s.cta}
      </div>
    </div>
  );
}

function CheckinScreen({ s }: { s: JourneyScreenCheckin }) {
  return (
    <div className="flex h-full flex-col px-4 pb-4">
      <h3 className="mt-1 text-[15px] font-semibold leading-snug text-zinc-900">
        {s.heading}
      </h3>

      <div className="mt-4 divide-y divide-zinc-100">
        {s.items.map((item) => (
          <div key={item} className="flex items-center gap-3 py-3.5">
            <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-orange-500 text-white">
              <Check size={11} strokeWidth={3} />
            </span>
            <p className="text-[12px] font-medium text-zinc-700">{item}</p>
          </div>
        ))}
      </div>

      <div className="mt-auto rounded-full bg-zinc-900 py-2.5 text-center text-[12px] font-semibold text-white">
        {s.cta}
      </div>
    </div>
  );
}

function VisitScreen({ s }: { s: JourneyScreenVisit }) {
  return (
    <div className="flex h-full flex-col px-4 pb-4 text-white">
      <div className="flex items-center justify-between gap-2">
        <p className="min-w-0 truncate text-[11px] font-medium text-zinc-300">
          {s.clinicianLabel}
        </p>
        <span className="flex-shrink-0 rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-medium tabular-nums text-zinc-300">
          {s.duration}
        </span>
      </div>

      <div className="relative mt-3 flex flex-1 items-center justify-center rounded-2xl bg-zinc-900">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-zinc-800">
          <Stethoscope size={24} className="text-zinc-400" />
        </span>
        <div className="absolute bottom-2.5 right-2.5 h-16 w-12 rounded-lg border border-white/10 bg-zinc-800" />
      </div>

      <p className="mt-3 text-center text-[10px] text-zinc-500">{s.caption}</p>

      <div className="mt-3 flex items-center justify-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10">
          <Mic size={14} className="text-zinc-300" />
        </span>
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10">
          <Video size={14} className="text-zinc-300" />
        </span>
        <span className="flex items-center gap-1.5 rounded-full bg-red-500 px-4 py-2 text-[11px] font-semibold text-white">
          <PhoneOff size={12} />
          {s.endLabel}
        </span>
      </div>
    </div>
  );
}

function PrescriptionScreen({ s }: { s: JourneyScreenPrescription }) {
  return (
    <div className="flex h-full flex-col px-4 pb-4">
      <h3 className="mt-1 text-[15px] font-semibold leading-snug text-zinc-900">
        {s.heading}
      </h3>

      <div className="mt-4 rounded-xl border border-zinc-200 p-3.5">
        <div className="flex items-start justify-between gap-2">
          <p className="text-[13px] font-semibold text-zinc-900">
            {s.medication}
          </p>
          <span className="flex flex-shrink-0 items-center gap-1 rounded-full bg-orange-50 px-2 py-0.5 text-[10px] font-semibold text-orange-600">
            <Check size={10} strokeWidth={3} />
            {s.status}
          </span>
        </div>
        <p className="mt-1.5 text-[11px] leading-relaxed text-zinc-500">
          {s.pharmacy}
        </p>
      </div>

      <div className="mt-auto rounded-full border border-zinc-300 py-2.5 text-center text-[12px] font-medium text-zinc-700">
        {s.cta}
      </div>
    </div>
  );
}

function FollowupScreen({ s }: { s: JourneyScreenFollowup }) {
  return (
    <div className="flex h-full flex-col px-4 pb-4">
      <h3 className="mt-1 text-[15px] font-semibold leading-snug text-zinc-900">
        {s.heading}
      </h3>

      <div className="mt-4 rounded-2xl rounded-tl-sm bg-zinc-100 p-3.5 text-[12px] leading-relaxed text-zinc-700">
        {s.message}
      </div>

      <div className="mt-4 space-y-2.5">
        {s.planItems.map((item) => (
          <div key={item} className="flex items-start gap-2.5">
            <span className="mt-[5px] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-orange-500" />
            <p className="text-[12px] font-medium text-zinc-700">{item}</p>
          </div>
        ))}
      </div>

      <div className="mt-auto rounded-full bg-orange-500 py-2.5 text-center text-[12px] font-semibold text-white">
        {s.cta}
      </div>
    </div>
  );
}

function ScreenForStep({ step }: { step: HealthTechJourneyStep }) {
  switch (step.id) {
    case "book":
      return <BookScreen s={step.screen} />;
    case "checkin":
      return <CheckinScreen s={step.screen} />;
    case "visit":
      return <VisitScreen s={step.screen} />;
    case "prescription":
      return <PrescriptionScreen s={step.screen} />;
    case "followup":
      return <FollowupScreen s={step.screen} />;
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// The CSS phone — bezel, dynamic island, status bar, home indicator. Screens
// swap with a short fade/slide; instant under reduced motion.
// ---------------------------------------------------------------------------
function PhoneFrame({ step }: { step: HealthTechJourneyStep }) {
  const prefersReduced = useReducedMotion();
  const dark = step.id === "visit";

  return (
    <div aria-hidden="true" className="mx-auto w-full max-w-[290px] select-none">
      <div className="rounded-[2.75rem] border border-zinc-800 bg-zinc-950 p-2.5 shadow-[0_24px_80px_-24px_rgba(0,0,0,0.45)]">
        <div className="relative aspect-[9/19] overflow-hidden rounded-[2.1rem] bg-white">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={step.id}
              className={`absolute inset-0 flex flex-col pt-12 ${
                dark ? "bg-zinc-950" : "bg-white"
              }`}
              initial={prefersReduced ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={prefersReduced ? undefined : { opacity: 0, y: -8 }}
              transition={{ duration: 0.35, ease: EASE }}
            >
              <ScreenForStep step={step} />
            </motion.div>
          </AnimatePresence>

          {/* Status bar + dynamic island (above the animating screen) */}
          <div className="pointer-events-none absolute inset-x-0 top-0 z-10">
            <div
              className={`flex items-center justify-between px-6 pt-3 text-[10px] font-semibold tabular-nums ${
                dark ? "text-white" : "text-zinc-900"
              }`}
            >
              <span>9:41</span>
              <span className="flex items-center gap-1">
                <span className="flex items-end gap-[2px]">
                  <span className={`h-[4px] w-[3px] rounded-[1px] ${dark ? "bg-white" : "bg-zinc-900"}`} />
                  <span className={`h-[6px] w-[3px] rounded-[1px] ${dark ? "bg-white" : "bg-zinc-900"}`} />
                  <span className={`h-[8px] w-[3px] rounded-[1px] ${dark ? "bg-white" : "bg-zinc-900"}`} />
                </span>
                <span
                  className={`ml-0.5 h-[9px] w-[18px] rounded-[3px] border p-[1.5px] ${
                    dark ? "border-white/50" : "border-zinc-400"
                  }`}
                >
                  <span className={`block h-full w-3/4 rounded-[1px] ${dark ? "bg-white" : "bg-zinc-900"}`} />
                </span>
              </span>
            </div>
            <div className="absolute left-1/2 top-2 h-5 w-20 -translate-x-1/2 rounded-full bg-zinc-950 ring-1 ring-black/40" />
          </div>

          {/* Home indicator */}
          <div
            className={`pointer-events-none absolute bottom-1.5 left-1/2 z-10 h-1 w-24 -translate-x-1/2 rounded-full ${
              dark ? "bg-white/30" : "bg-zinc-900/20"
            }`}
          />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section — heading, clickable step rows (hairline list), phone. Steps
// auto-advance while the section is in view; any click pauses auto-play.
// ---------------------------------------------------------------------------
export default function TelemedicineJourney({ data }: { data: HealthTechJourney }) {
  const prefersReduced = useReducedMotion();
  const sectionRef = useRef<HTMLElement>(null);
  const inView = useInView(sectionRef, { amount: 0.35 });

  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const steps = data.steps ?? [];
  const autoplay = inView && !paused && !prefersReduced && steps.length > 1;

  useEffect(() => {
    if (!autoplay) return;
    const timer = setInterval(
      () => setActiveIndex((i) => (i + 1) % steps.length),
      AUTOPLAY_MS,
    );
    return () => clearInterval(timer);
  }, [autoplay, steps.length]);

  if (steps.length === 0) return null;
  const activeStep = steps[Math.min(activeIndex, steps.length - 1)];

  return (
    <section
      ref={sectionRef}
      className="relative z-10 bg-transparent px-5 py-24 text-foreground sm:px-8 md:py-36 lg:px-12"
    >
      <div className="mx-auto max-w-[88rem]">
        <div className="max-w-2xl">
          <h2 className="font-display text-balance text-3xl font-semibold leading-[1.08] tracking-[-0.02em] md:text-4xl">
            {data.title} {data.boldTitle}
          </h2>
          <p className="mt-5 text-base leading-relaxed text-foreground/80 md:text-lg">
            {data.description}
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Phone first in DOM so it leads on mobile; right column on lg */}
          <div className="lg:order-2 lg:col-span-6">
            <PhoneFrame step={activeStep} />
          </div>

          <div className="lg:order-1 lg:col-span-6">
            {steps.map((step, index) => {
              const isActive = index === activeIndex;
              return (
                <div
                  key={step.id}
                  className="relative border-t border-border last:border-b"
                >
                  <button
                    type="button"
                    onClick={() => {
                      setActiveIndex(index);
                      setPaused(true);
                    }}
                    aria-current={isActive || undefined}
                    className="group flex w-full items-baseline gap-5 py-5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 md:py-6"
                  >
                    <span
                      aria-hidden="true"
                      className={`font-display flex-shrink-0 text-sm font-medium tabular-nums transition-colors duration-300 ${
                        isActive ? "text-orange-500" : "text-foreground/40"
                      }`}
                    >
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span
                        className={`font-display block text-lg font-semibold tracking-[-0.01em] transition-colors duration-300 md:text-xl ${
                          isActive
                            ? "text-orange-500"
                            : "text-foreground group-hover:text-orange-500"
                        }`}
                      >
                        {step.label}
                      </span>
                      <AnimatePresence initial={false}>
                        {isActive && (
                          <motion.span
                            className="block overflow-hidden"
                            initial={prefersReduced ? false : { height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={prefersReduced ? undefined : { height: 0, opacity: 0 }}
                            transition={{ duration: 0.4, ease: EASE }}
                          >
                            <span className="block max-w-lg pt-2.5 text-base leading-relaxed text-foreground/80">
                              {step.description}
                            </span>
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </span>
                  </button>

                  {/* Auto-play progress rail on the active row */}
                  {isActive && autoplay && (
                    <motion.span
                      aria-hidden="true"
                      key={`rail-${activeIndex}`}
                      className="absolute inset-x-0 -bottom-px h-0.5 origin-left bg-orange-500"
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      transition={{ duration: AUTOPLAY_MS / 1000, ease: "linear" }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
